import { useRef, type KeyboardEvent } from "react";
import "./segmented.css";

/** `count`: un numero accanto all'etichetta, che fa parte del nome del pulsante (le schede dei Task: «Oggi 3»). */
type Option = { value: string; label: string; count?: string };

type Props = {
  /** Il nome del gruppo per gli screen reader («Cosa vuoi vedere»). */
  label: string;
  options: readonly Option[];
  value: string;
  onChange: (value: string) => void;
};

// Pulsanti che si escludono a vicenda, con `aria-pressed`. Un solo punto di sosta per il Tab (il pulsante
// scelto) e le frecce spostano la scelta, come in un gruppo di opzioni: la scelta segue il focus.
export function Segmented({ label, options, value, onChange }: Props) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const chosen = options.findIndex((option) => option.value === value);
  // Se il valore non è fra le opzioni, il primo pulsante resta raggiungibile con il Tab.
  const tabStop = chosen === -1 ? 0 : chosen;

  function move(target: number) {
    const next = options[target];
    if (!next) return;
    onChange(next.value);
    buttons.current[target]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = options.length - 1;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        move(index === last ? 0 : index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        move(index === 0 ? last : index - 1);
        break;
      case "Home":
        move(0);
        break;
      case "End":
        move(last);
        break;
      default:
        return;
    }
    // Le frecce non devono anche scorrere la pagina.
    event.preventDefault();
  }

  return (
    <div className="g-segmented" role="group" aria-label={label}>
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(element) => {
            buttons.current[index] = element;
          }}
          type="button"
          aria-pressed={option.value === value}
          tabIndex={index === tabStop ? 0 : -1}
          onClick={() => onChange(option.value)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          {option.label}
          {option.count === undefined ? null : (
            <>
              {" "}
              <span className="g-segmented__count">{option.count}</span>
            </>
          )}
        </button>
      ))}
    </div>
  );
}
